import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Search, Edit, Trash2, Upload, Download, X, Eye, EyeOff,
  RefreshCw, User, Phone, Hash, Loader2, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, ToggleLeft, ToggleRight, Copy, CheckSquare, Square
} from 'lucide-react';

const ITEMS_PER_PAGE = 15;

const STATUS_COLORS = {
  Pending:  'bg-amber-100 text-amber-700 border border-amber-200',
  Approved: 'bg-green-100 text-green-700 border border-green-200',
  Rejected: 'bg-red-100 text-red-600 border border-red-200',
};

const VERIFIED_BADGE = {
  true:  'bg-green-100 text-green-700 border border-green-200',
  false: 'bg-red-100 text-red-600 border border-red-200',
};

export default function StudentsPage() {
  const { isSuperAdmin } = useAuth();
  const superadmin = isSuperAdmin();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [createdStudent, setCreatedStudent] = useState(null);
  const [approvalResult, setApprovalResult] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const fileInputRef = useRef();

  const [form, setForm] = useState({
    name: '', fatherName: '', dob: '', collegeName: '', locality: '',
    phone: '', email: '', address: '', hallTicketNumber: '', photo: null
  });

  useEffect(() => { fetchStudents(); }, [search, page, filterStatus]);

  const fetchStudents = async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append('search', search);
      if (filterStatus) params.append('registrationStatus', filterStatus);
      const { data } = await api.get(`/students?${params}`);
      setStudents(data.data);
      setPagination(data.pagination);
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id, name) => {
    if (!window.confirm(`Approve "${name}"? A Hall Ticket and password will be generated.`)) return;
    try {
      const { data } = await api.patch(`/students/${id}/approve`);
      toast.success(`${name} approved!`);
      setApprovalResult(data.data);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
    }
  };

  const handleReject = async (id, name) => {
    if (!window.confirm(`Reject "${name}"?`)) return;
    try {
      await api.patch(`/students/${id}/reject`);
      toast.success(`${name} rejected`);
      fetchStudents();
    } catch (err) {
      toast.error('Rejection failed');
    }
  };

  const handleToggleStatus = async (id, name, isActive) => {
    try {
      await api.patch(`/students/${id}/toggle-status`);
      toast.success(`${name} ${isActive ? 'deactivated' : 'activated'}`);
      fetchStudents();
    } catch {
      toast.error('Status toggle failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error('Name and phone are required');
    setFormLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('phone', form.phone);
      fd.append('email', form.email);
      if (form.fatherName)     fd.append('fatherName', form.fatherName);
      if (form.dob)            fd.append('dob', form.dob);
      if (form.collegeName)    fd.append('collegeName', form.collegeName);
      if (form.locality)       fd.append('locality', form.locality);
      if (form.address)        fd.append('address', form.address);
      if (form.hallTicketNumber) fd.append('hallTicketNumber', form.hallTicketNumber);
      if (form.photo)          fd.append('photo', form.photo);

      if (editStudent) {
        await api.put(`/students/${editStudent._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Student updated successfully');
        setShowModal(false);
        setEditStudent(null);
      } else {
        const { data } = await api.post('/students', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCreatedStudent(data.data);
        toast.success('Student created successfully!');
      }
      resetForm();
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete student "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success('Student deleted');
      fetchStudents();
    } catch {
      toast.error('Failed to delete student');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === students.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(students.map(s => s._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/students/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} student(s) deleted permanently`);
      setSelectedIds([]);
      setShowBulkDeleteModal(false);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk delete failed');
    }
  };

  const handleResetPassword = async (id, name) => {
    if (!window.confirm(`Reset password for "${name}"?`)) return;
    try {
      const { data } = await api.post(`/students/${id}/reset-password`);
      toast.success(`New password: ${data.newPassword}`, { duration: 8000 });
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/students/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.message);
      fetchStudents();
      if (data.data.success.length > 0) {
        const csvContent = data.data.success.map(s => `${s.name},${s.hallTicketNumber},${s.generatedPassword}`).join('\n');
        const blob = new Blob([`Name,Hall Ticket,Password\n${csvContent}`], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'students_credentials.csv'; a.click();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadLoading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'Name,Phone,Email,Hall Ticket\nArjun Reddy,9876543210,arjun@example.com,\nPriya Sharma,9876543211,priya@example.com,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'students_template.csv'; a.click();
  };

  const exportStudents = async (format) => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const res = await api.get(`/students/export?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Students_${Date.now()}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No approved students with hall tickets to export');
      } else {
        toast.error('Export failed');
      }
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (student) => {
    setEditStudent(student);
    setForm({
      name: student.name,
      fatherName: student.fatherName || '',
      dob: student.dob ? student.dob.slice(0, 10) : '',
      collegeName: student.collegeName || '',
      locality: student.locality || '',
      phone: student.phone,
      email: student.email || '',
      address: student.address || '',
      hallTicketNumber: student.hallTicketNumber || '',
      photo: null
    });
    setShowModal(true);
    setCreatedStudent(null);
  };

  const resetForm = () => setForm({
    name: '', fatherName: '', dob: '', collegeName: '', locality: '',
    phone: '', email: '', address: '', hallTicketNumber: '', photo: null
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  const statusCounts = students.reduce((acc, s) => {
    acc[s.registrationStatus] = (acc[s.registrationStatus] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
          <p className="text-gray-500 mt-1">{pagination.total || 0} students registered</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-secondary flex items-center gap-2 text-sm">
            <Download size={16} /> Template
          </button>
          <label className="btn-secondary flex items-center gap-2 text-sm cursor-pointer">
            {uploadLoading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            Bulk Upload
            <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} ref={fileInputRef} />
          </label>
          <div className="relative">
            <button onClick={() => setShowExportMenu(!showExportMenu)} disabled={exporting}
              className="btn-secondary flex items-center gap-2 text-sm disabled:opacity-60">
              {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
              Export
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 animate-fade-in">
                  <button onClick={() => exportStudents('csv')}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <span className="text-xs font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">CSV</span> Spreadsheet
                  </button>
                  <button onClick={() => exportStudents('pdf')}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <span className="text-xs font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">PDF</span> Document
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={() => { setShowModal(true); setEditStudent(null); setCreatedStudent(null); resetForm(); }}
            className="btn-primary flex items-center gap-2 text-sm">
            <Plus size={16} /> Add Student
          </button>
        </div>
      </div>

      {/* Approval Result Banner */}
      {approvalResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-green-800">✅ Student Approved — Share these credentials:</p>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-600 w-32">Hall Ticket:</span>
                  <span className="font-mono font-bold text-gray-900 bg-white border border-gray-200 px-2 py-0.5 rounded">{approvalResult.hallTicketNumber}</span>
                  <button onClick={() => copyToClipboard(approvalResult.hallTicketNumber)}><Copy size={14} className="text-gray-400 hover:text-gray-600" /></button>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-600 w-32">Password:</span>
                  <span className="font-mono font-bold text-green-700 bg-white border border-green-200 px-2 py-0.5 rounded">{approvalResult.generatedPassword}</span>
                  <button onClick={() => copyToClipboard(approvalResult.generatedPassword)}><Copy size={14} className="text-gray-400 hover:text-gray-600" /></button>
                </div>
              </div>
            </div>
            <button onClick={() => setApprovalResult(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {superadmin && selectedIds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-red-700">{selectedIds.length} student(s) selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-lg hover:bg-white/60">
              Clear
            </button>
            <button onClick={() => setShowBulkDeleteModal(true)}
              className="flex items-center gap-2 text-sm bg-red-600 text-white px-4 py-1.5 rounded-lg hover:bg-red-700 transition-colors">
              <Trash2 size={14} /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input type="text" placeholder="Search by name, hall ticket, or phone..."
            className="input-field pl-10" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="input-field w-auto" value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {superadmin && (
                  <th className="px-5 py-3 w-10">
                    <button onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-700">
                      {students.length > 0 && selectedIds.length === students.length
                        ? <CheckSquare size={18} className="text-red-500" />
                        : <Square size={18} />}
                    </button>
                  </th>
                )}
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">#</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Student</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Hall Ticket</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Phone</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Verified</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Status</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Active</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-16"><div className="loader mx-auto"></div></td></tr>
              ) : students.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 text-gray-400">
                  <User size={48} className="mx-auto mb-2 opacity-30" /><p>No students found</p>
                </td></tr>
                ) : (
                students.map((student, i) => (
                  <tr key={student._id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedIds.includes(student._id) ? 'bg-red-50/40' : ''}`}>
                    {superadmin && (
                      <td className="px-5 py-3">
                        <button onClick={() => toggleSelect(student._id)} className="text-gray-400 hover:text-gray-700">
                          {selectedIds.includes(student._id)
                            ? <CheckSquare size={18} className="text-red-500" />
                            : <Square size={18} />}
                        </button>
                      </td>
                    )}
                    <td className="px-5 py-3 text-gray-400">{(page - 1) * ITEMS_PER_PAGE + i + 1}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {student.photo ? (
                          <img src={student.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-semibold text-xs">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">{student.name}</p>
                          <p className="text-xs text-gray-400">{student.email || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {student.hallTicketNumber
                        ? <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">{student.hallTicketNumber}</span>
                        : <span className="text-xs text-gray-400 italic">Not assigned</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-600">{student.phone}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${VERIFIED_BADGE[student.isVerified]}`}>
                        {student.isVerified ? '✓ Verified' : '✗ Not Verified'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[student.registrationStatus] || STATUS_COLORS.Pending}`}>
                        {student.registrationStatus || 'Pending'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => handleToggleStatus(student._id, student.name, student.isActive)}
                        className={`transition-colors ${student.isActive ? 'text-green-500 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
                        title={student.isActive ? 'Deactivate' : 'Activate'}>
                        {student.isActive ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {student.registrationStatus !== 'Approved' && (
                          <button onClick={() => handleApprove(student._id, student.name)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Approve">
                            <CheckCircle size={15} />
                          </button>
                        )}
                        {student.registrationStatus !== 'Rejected' && (
                          <button onClick={() => handleReject(student._id, student.name)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Reject">
                            <XCircle size={15} />
                          </button>
                        )}
                        <button onClick={() => handleResetPassword(student._id, student.name)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Reset Password">
                          <RefreshCw size={15} />
                        </button>
                        <button onClick={() => openEdit(student)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <Edit size={15} />
                        </button>
                        {/* Delete — superadmin only */}
                        {superadmin && (
                          <button onClick={() => handleDelete(student._id, student.name)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary py-1.5 px-3 disabled:opacity-40">
                <ChevronLeft size={16} />
              </button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} className="btn-secondary py-1.5 px-3 disabled:opacity-40">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Bulk Delete Confirmation Modal ──────────────────────────── */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Delete {selectedIds.length} Student(s)?</h3>
                <p className="text-sm text-gray-500">This action is permanent and cannot be undone.</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-5">
              <p className="text-xs text-red-700">
                All data including exam results, attempts, and uploaded photos will be permanently removed from the database.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowBulkDeleteModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleBulkDelete}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2">
                <Trash2 size={15} /> Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Student Modal ─────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-fade-in max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="font-bold text-gray-900">{editStudent ? 'Edit Student' : 'Add New Student'}</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {editStudent ? 'Update student details' : 'Manually add a student — credentials generated automatically'}
                </p>
              </div>
              <button onClick={() => { setShowModal(false); setCreatedStudent(null); }}
                className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            {/* Credentials success screen */}
            {createdStudent ? (
              <div className="p-6 space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <div className="text-green-600 font-bold text-lg mb-1">✅ Student Created!</div>
                  <p className="text-sm text-gray-600">Share these login credentials with the student:</p>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Name',        val: createdStudent.name },
                    { label: 'Hall Ticket', val: createdStudent.hallTicketNumber, mono: true },
                    { label: 'Password',    val: createdStudent.generatedPassword, mono: true, green: true },
                  ].map(({ label, val, mono, green }) => (
                    <div key={label} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600 text-sm font-medium w-28">{label}:</span>
                      <span className={`font-bold text-sm ${mono ? 'font-mono' : ''} ${green ? 'text-green-700' : 'text-gray-900'}`}>{val}</span>
                      <button type="button" onClick={() => copyToClipboard(val)}>
                        <Copy size={13} className="text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
                <button onClick={() => { setShowModal(false); setCreatedStudent(null); }} className="btn-primary w-full">Done</button>
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">

                {/* Section: Identity */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Identity</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input type="text" className="input-field" placeholder="e.g. Arjun Reddy"
                      value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                    <input type="text" className="input-field" placeholder="Father's full name"
                      value={form.fatherName} onChange={e => setForm({...form, fatherName: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input type="date" className="input-field" max={new Date().toISOString().split('T')[0]}
                      value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} />
                  </div>
                </div>

                {/* Section: Contact */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-2">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input type="tel" className="input-field" placeholder="9876543210" maxLength={10}
                      value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" className="input-field" placeholder="student@example.com"
                      value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input type="text" className="input-field" placeholder="Residential address"
                      value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                  </div>
                </div>

                {/* Section: Academic */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-2">Academic</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">College / Institute</label>
                    <input type="text" className="input-field" placeholder="e.g. BITS Vizag"
                      value={form.collegeName} onChange={e => setForm({...form, collegeName: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
                    <input type="text" className="input-field" placeholder="e.g. Vizag"
                      value={form.locality} onChange={e => setForm({...form, locality: e.target.value})} />
                  </div>
                  {!editStudent && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hall Ticket <span className="text-gray-400 text-xs">(auto if empty)</span></label>
                      <input type="text" className="input-field font-mono uppercase" placeholder="EAM2024XXX"
                        value={form.hallTicketNumber} onChange={e => setForm({...form, hallTicketNumber: e.target.value.toUpperCase()})} />
                    </div>
                  )}
                </div>

                {/* Photo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photo <span className="text-gray-400 text-xs">(optional)</span></label>
                  <input type="file" accept="image/*" className="input-field text-sm py-1.5"
                    onChange={e => setForm({...form, photo: e.target.files[0]})} />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary flex-1">Cancel</button>
                  <button type="submit" disabled={formLoading} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    {formLoading ? <Loader2 className="animate-spin" size={16} /> : null}
                    {editStudent ? 'Update Student' : 'Create Student'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
